/**
 * Renders the brand row on PDP, in three tiers.
 *
 * 1. **Telehealth** (`c_ownerClass === "Telehealth"`) — a fixed "Shop more
 *    Patiently" link to the Patiently brand landing page. Checked FIRST, and
 *    deliberately ignores every catalog brand field: on telehealth SKUs the
 *    `brand` attribute holds the MEDICATION name, so both tiers below render
 *    the wrong thing ("Shop more Mounjaro", linking to a keyword search).
 *    See `~/hec/telehealth/brand` for why this is constant-driven. ET-369.
 * 2. **Enriched** — SCAPI `c_brandName` / `c_brandURL` / `c_brandLeadingCopy`
 *    populated by the `hec-brand-name` custom-object hook (SFCC-10167).
 * 3. **Fallback** — a "Shop more {brand}" search link when the enriched fields
 *    are absent (e.g. brand has no custom object configured).
 *
 * Renders nothing if there's no brand at all.
 *
 * Tier 1 has to precede tier 2, not just tier 3: a telehealth brand with a
 * `hec-brand-name` custom object configured would otherwise take the enriched
 * path and reintroduce the bug with no code change.
 *
 * Tier 1 lives HERE rather than being composed at the callers that know a
 * product is telehealth, deliberately: the bug this fixes was a caller
 * rendering the catalog `brand` verbatim, so a design where each new caller
 * must remember to branch first has the original defect as its default. Every
 * caller passes a product shape that already carries `c_ownerClass`, so the
 * tier costs them nothing and cannot be forgotten.
 *
 * Not PDP-only, despite the name: the cart's Edit dialog
 * (`routes/resources/components/edit-line-item-dialog.tsx`) shares this
 * function, so tier 1 applies there too. Unreachable for a telehealth line
 * today, but by the CART ROW, not by this dialog: a telehealth line renders
 * `TelehealthCartRow`, whose "Edit plan" control is a `<Link>` into
 * `/intake/{slug}/plan` — the generic `LineItemActions` "Edit" button that
 * opens this dialog only renders on `CartRowLayout` (cart.tsx). (ET-212
 * deleted `TelehealthLineItemActions`, the component this note used to cite.)
 * The tier is the more correct row here anyway if that routing ever changes.
 *
 * Divergence from SFRA: `productBrand.isml` renders nothing when the
 * custom object is missing. We preserve the existing Workers behavior
 * ("Shop more {brand}" search link) on fallback to avoid regressing
 * products that already display a brand link on the live Workers cart.
 * Reviewer / PO can tighten this to match SFRA exactly post-launch.
 *
 * Every surface that shows a brand row MUST go through this helper. Dialog
 * callers pass `onNavigate` so the modal closes the moment the shopper
 * activates the link, instead of sitting over the destination for the whole
 * loader round-trip; they should ALSO mount `useCloseOnNavigation`, which
 * covers every other link inside the dialog (COM-104).
 *
 * No `prefetch` on either link on purpose: the brand name sits directly
 * under the H1 on the highest-traffic surfaces (PDP ×2, quick view, cart
 * Quick Edit), and an intent prefetch there fires a catch-all loader + CIO
 * browse for a PLP most shoppers never open (product-data-caching-audit §3.3).
 */
import type { MouseEvent } from "react";

import { HybridLink } from "~/hec/components/ui/hybrid-link";
import {
  TELEHEALTH_BRAND_LANDING_PATH,
  TELEHEALTH_BRAND_NAME,
} from "~/hec/telehealth/brand";
import { isTelehealthProduct } from "~/hec/types/product";
import { searchUrlBuilder } from "~/lib/utils/url";

type BrandRowInput = {
  brand?: string;
  c_brandName?: string;
  c_brandURL?: string;
  c_brandLeadingCopy?: string;
  c_ownerClass?: string | null;
};

type BrandRowOptions = {
  /** Fired when the shopper activates the link with a plain left click.
   *  Dialog surfaces pass their close handler so an SPA navigation doesn't
   *  leave the modal mounted over the destination. */
  onNavigate?: () => void;
};

/**
 * Mirrors React Router's own `shouldProcessLinkClick`: only a plain,
 * unmodified left click navigates the current tab. Cmd/Ctrl/Shift/Alt and
 * middle clicks open a new tab or window and leave this page (and therefore
 * the dialog) where it is, so they must not fire `onNavigate`.
 */
function toNavigateHandler(onNavigate: (() => void) | undefined) {
  if (!onNavigate) return undefined;
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
      return;
    onNavigate();
  };
}

export function renderBrandRow(
  product: BrandRowInput,
  options?: BrandRowOptions,
) {
  const { brand, c_brandName, c_brandURL, c_brandLeadingCopy } = product;
  const onClick = toNavigateHandler(options?.onNavigate);
  if (isTelehealthProduct(product)) {
    return (
      <p className="text-sm text-foreground" data-testid="pdp-brand-telehealth">
        Shop more{" "}
        <HybridLink
          to={TELEHEALTH_BRAND_LANDING_PATH}
          prefetch="intent"
          onClick={onClick}
          className="font-normal text-emphasis underline"
        >
          {TELEHEALTH_BRAND_NAME}
        </HybridLink>
      </p>
    );
  }
  if (c_brandName && c_brandURL) {
    return (
      <p className="text-sm text-foreground" data-testid="pdp-brand-enriched">
        {c_brandLeadingCopy ? (
          <span className="pdp-brand-leading-copy">{c_brandLeadingCopy} </span>
        ) : null}
        <HybridLink
          to={c_brandURL}
          onClick={onClick}
          className="font-normal text-emphasis underline"
        >
          {c_brandName}
        </HybridLink>
      </p>
    );
  }
  if (brand) {
    return (
      <p className="text-sm text-foreground" data-testid="pdp-brand-fallback">
        Shop more{" "}
        <HybridLink
          to={searchUrlBuilder(brand)}
          onClick={onClick}
          className="font-normal text-emphasis underline"
        >
          {brand}
        </HybridLink>
      </p>
    );
  }
  return null;
}
